import { z } from 'zod';

const REQUEST = 'move-helpdesk:credential-request';
const RESULT = 'move-helpdesk:credential-result';
const CHANGED = 'move-helpdesk:identity-changed';
const EXPIRY_MARGIN_MS = 15_000;
const TIMEOUT_MS = 15_000;

const common = z.object({ version: z.literal(1), generation: z.number().int().nonnegative() });
const result = z.discriminatedUnion('status', [
  common
    .extend({
      type: z.literal(RESULT),
      requestId: z.uuid(),
      status: z.literal('authenticated'),
      credential: z.string().min(1).max(8192),
      expiresAt: z.iso.datetime(),
    })
    .strict(),
  common
    .extend({ type: z.literal(RESULT), requestId: z.uuid(), status: z.literal('guest') })
    .strict(),
  common
    .extend({ type: z.literal(RESULT), requestId: z.uuid(), status: z.literal('unavailable') })
    .strict(),
]);
const changed = common.extend({ type: z.literal(CHANGED) }).strict();

export interface NativeSnapshot {
  status: 'resolving' | 'guest' | 'connected' | 'reconnect';
  generation: number;
  epoch: number;
}

export interface NativeAccessCredential {
  token: string;
  epoch: number;
}

interface NativeBridge {
  postMessage(message: string): void;
}

declare global {
  interface Window {
    ReactNativeWebView?: NativeBridge;
  }
}

/** The native app supplies only a short Helpdesk credential to its trusted hosted page. */
export class NativeSession {
  private value: NativeSnapshot = { status: 'resolving', generation: 0, epoch: 0 };
  private listeners = new Set<() => void>();
  private access: { credential: string; expiresAt: string } | null = null;
  private actor: string | null = null;
  private hostGeneration = -1;
  private pending: { requestId: string; resolve: () => void; timer: number } | null = null;
  private pendingPromise: Promise<void> | null = null;
  private disposed = false;

  constructor(private readonly bridge: NativeBridge) {
    window.addEventListener('message', this.onMessage);
    void this.request();
  }

  snapshot = (): NativeSnapshot => this.value;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  hasAccountActor = (): boolean => this.actor !== null;

  private update(status: NativeSnapshot['status'], actorChanged = false, proofLost = false): void {
    this.value = {
      status,
      generation: this.value.generation + (actorChanged ? 1 : 0),
      epoch: this.value.epoch + (actorChanged || proofLost ? 1 : 0),
    };
    for (const listener of this.listeners) listener();
  }

  private loseProof(status: NativeSnapshot['status']): void {
    const proofLost = this.access !== null || this.value.status === 'connected';
    this.access = null;
    this.update(status, false, proofLost);
  }

  private cancelPending(): void {
    if (!this.pending) return;
    window.clearTimeout(this.pending.timer);
    this.pending.resolve();
    this.pending = null;
    this.pendingPromise = null;
  }

  private clear(status: NativeSnapshot['status']): void {
    const actorChanged = this.actor !== null || this.value.status !== status;
    this.cancelPending();
    const proofLost = this.access !== null;
    this.access = null;
    this.actor = null;
    this.update(status, actorChanged, proofLost);
  }

  private onMessage = (event: MessageEvent): void => {
    if (this.disposed || (event.source !== null && event.source !== window)) return;
    let data: unknown = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data) as unknown;
      } catch {
        return;
      }
    }
    const identity = changed.safeParse(data);
    if (identity.success) {
      if (identity.data.generation <= this.hostGeneration) return;
      this.hostGeneration = identity.data.generation;
      this.clear('resolving');
      void this.request();
      return;
    }
    const parsed = result.safeParse(data);
    if (
      !parsed.success ||
      parsed.data.requestId !== this.pending?.requestId ||
      parsed.data.generation < this.hostGeneration
    )
      return;
    this.hostGeneration = parsed.data.generation;
    this.cancelPending();
    if (parsed.data.status === 'guest') {
      this.clear('guest');
      return;
    }
    if (parsed.data.status === 'unavailable') {
      this.loseProof('reconnect');
      return;
    }
    if (Date.parse(parsed.data.expiresAt) <= Date.now() + EXPIRY_MARGIN_MS) {
      this.loseProof('reconnect');
      return;
    }
    const subject = tokenSubject(parsed.data.credential);
    if (!subject) {
      this.loseProof('reconnect');
      return;
    }
    const actorChanged = this.actor !== subject;
    this.actor = subject;
    this.access = { credential: parsed.data.credential, expiresAt: parsed.data.expiresAt };
    this.update('connected', actorChanged);
  };

  private request(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    if (this.pendingPromise) return this.pendingPromise;
    const requestId = crypto.randomUUID();
    const promise = new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        if (this.pending?.requestId !== requestId) return;
        this.cancelPending();
        this.loseProof('reconnect');
      }, TIMEOUT_MS);
      this.pending = { requestId, resolve, timer };
    });
    this.pendingPromise = promise;
    queueMicrotask(() => {
      if (this.pending?.requestId === requestId)
        this.bridge.postMessage(JSON.stringify({ type: REQUEST, version: 1, requestId }));
    });
    return promise;
  }

  async getAccessCredential(): Promise<NativeAccessCredential> {
    if (this.disposed || this.actor === null) throw new Error('Native Helpdesk actor unavailable.');
    if (this.access && Date.parse(this.access.expiresAt) > Date.now() + EXPIRY_MARGIN_MS)
      return { token: this.access.credential, epoch: this.value.epoch };
    if (this.value.status === 'reconnect' || this.value.status === 'guest')
      throw new Error('Native Helpdesk connection unavailable.');
    this.loseProof('resolving');
    const epoch = this.value.epoch;
    await this.request();
    if (
      this.value.epoch !== epoch ||
      !this.access ||
      Date.parse(this.access.expiresAt) <= Date.now() + EXPIRY_MARGIN_MS
    )
      throw new Error('Native Helpdesk connection unavailable.');
    return { token: this.access.credential, epoch: this.value.epoch };
  }

  async getAccessToken(): Promise<string> {
    return (await this.getAccessCredential()).token;
  }

  reject(): void {
    this.loseProof('reconnect');
  }

  retry(): void {
    if (this.disposed) return;
    this.loseProof('resolving');
    void this.request();
  }

  dispose(): void {
    this.disposed = true;
    this.clear('guest');
    window.removeEventListener('message', this.onMessage);
    this.listeners.clear();
  }
}

function tokenSubject(credential: string): string | null {
  try {
    const payload = credential.split('.')[1];
    if (!payload) return null;
    const parsed = z
      .object({ sub: z.uuid() })
      .safeParse(JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as unknown);
    return parsed.success ? parsed.data.sub : null;
  } catch {
    return null;
  }
}

/** The bridge is enabled only on the dedicated first-party Helpdesk document. */
export function nativeHelpdeskBridge(): NativeBridge | null {
  if (
    window.location.protocol !== 'https:' ||
    !/^\/(?:en|ru)\/helpdesk\/?$/.test(window.location.pathname) ||
    window.location.search !== '' ||
    window.location.hash !== ''
  )
    return null;
  return window.ReactNativeWebView ?? null;
}
